import type { SquareSavedCard } from '~entities/payment/api/square-payment-methods';

/** Shared shape for V2 `/v2/payment/cards` (Stripe or Square). */
export type SavedPaymentMethodDisplay = Pick<
  SquareSavedCard,
  'id' | 'type' | 'brand' | 'last4' | 'exp_month' | 'exp_year' | 'isDefault'
> & {
  bankName?: string;
  bsb?: string;
};

const CARD_LINK_LABEL = 'Card Link';

const isBankType = (type: string) =>
  type === 'us_bank_account' || type === 'au_becs_debit';

const formatBrand = (brand: string | undefined, fallback: string): string => {
  const trimmed = brand?.trim();
  if (!trimmed) return fallback;
  if (trimmed.toLowerCase() === 'link') return CARD_LINK_LABEL;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

export const formatSavedPaymentMethodTitle = (
  method: SavedPaymentMethodDisplay,
): string => {
  if (method.type === 'link') {
    const brandLower = method.brand?.toLowerCase();
    if (
      method.last4 &&
      method.brand &&
      brandLower !== 'link' &&
      brandLower !== 'card link'
    ) {
      return `${CARD_LINK_LABEL} · ${formatBrand(method.brand, 'Card')} ending in ${method.last4}`;
    }
    if (method.last4) {
      return `${CARD_LINK_LABEL} · ****${method.last4}`;
    }
    return CARD_LINK_LABEL;
  }

  if (method.type === 'au_becs_debit') {
    return method.bankName?.trim() || 'BECS Direct Debit';
  }

  if (method.type === 'us_bank_account') {
    return method.brand?.trim() || method.bankName?.trim() || 'Bank account';
  }

  const brand = formatBrand(method.brand, 'Card');
  return method.last4 ? `${brand} ending in ${method.last4}` : brand;
};

export const formatSavedPaymentMethodSubtitle = (
  method: SavedPaymentMethodDisplay,
): string | null => {
  if (method.type === 'au_becs_debit') {
    const parts: string[] = [];
    if (method.bsb) parts.push(`BSB: ${method.bsb}`);
    if (method.last4) parts.push(`Account ending in ${method.last4}`);
    return parts.length > 0 ? parts.join(' • ') : null;
  }

  if (method.type === 'us_bank_account') {
    const bank = method.bankName?.trim();
    if (bank && method.last4) {
      return `${bank} ending in ${method.last4}`;
    }
    if (method.last4) {
      return `Ending in ${method.last4}`;
    }
    return bank || null;
  }

  if (method.type === 'link') {
    if (method.exp_month && method.exp_year) {
      return `Expires ${String(method.exp_month).padStart(2, '0')}/${method.exp_year}`;
    }
    return 'Saved with Card Link';
  }

  if (method.exp_month && method.exp_year) {
    return `${String(method.exp_month).padStart(2, '0')}/${method.exp_year}`;
  }

  return null;
};

export type SavedPaymentMethodIconKind = 'card' | 'bank' | 'link';

export const getSavedPaymentMethodIconKind = (
  type: string,
): SavedPaymentMethodIconKind => {
  if (isBankType(type)) return 'bank';
  if (type === 'link') return 'link';
  return 'card';
};
